'use client';

import {
  FileItemActionSlot,
  FileItemActionsSlot,
  FileItemDescriptionSlot,
  FileItemDownloadActionSlot,
  FileItemFileTypeSlot,
  FileItemProgressSlot,
  FileItemRemoveActionSlot,
  FileItemTitleSlot,
} from '@magicschool/spellbook/components/FileItem/slots';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';

export type FileItemProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof fileItemVariants> & {
    /** File type — extension ("pdf", "docx") or MIME type ("application/pdf", "image/jpeg"). Resolved internally. */
    fileType: string;
    /**
     * Display mode for the file type indicator
     * - 'label': Shows file type as an uppercase text label (default)
     * - 'icon': Shows file type as an icon with colored background
     */
    indicator?: 'label' | 'icon';
    /**
     * Status of the file item
     * - 'default': Normal display
     * - 'error': Shows error icon instead of file type icon
     */
    status?: 'default' | 'error';
    /** Whether the file item is loading. */
    isLoading?: boolean;
    /**
     * Upload progress (0-100). When provided, shows a progress bar below the description.
     * Typically used with Dropzone's fileProcessing feature.
     */
    progress?: number;
    /** Configuration for component default text content, labels, placeholders, and screen reader announcements. */
    text?: {
      /** Text to display in file description when the file is loading. */
      loading?: string;
      /** Accessible label for the file type icon (icon indicator mode). Overrides the auto-detected category label. */
      fileTypeLabel?: string;
    };
  };

export const fileItemVariants = cva(['flex', 'items-center', 'gap-3'], {
  variants: {
    /**
     * Visual variant
     * - 'card': Standalone card with border and background (default)
     * - 'row': Borderless row for use inside a parent container
     */
    variant: {
      card: ['px-3', 'py-2', 'rounded-card-md', 'border', 'border-border-default', 'bg-surface-background-0'],
      row: ['border-0', 'bg-transparent', 'rounded-none'],
    },
    /** Whether the file item is visually disabled. */
    disabled: {
      true: 'cursor-not-allowed opacity-70',
      false: '',
    },
  },
  compoundVariants: [
    // Only show disabled border on disabled card variant
    {
      variant: 'card',
      disabled: true,
      className: 'border-border-disabled-foreground',
    },
  ],
  defaultVariants: {
    variant: 'card',
    disabled: false,
  },
});

const FileItemBase: React.FC<FileItemProps> = ({
  children,
  className,
  variant = 'card',
  disabled = false,
  fileType,
  indicator = 'label',
  status = 'default',
  isLoading = false,
  progress,
  text: textProp,
  ...props
}) => {
  const text = { loading: 'Processing...', ...textProp };

  const { slots } = useSlots(children, {
    fileType: {
      Component: FileItemFileTypeSlot,
      props: { children: fileType, disabled, isLoading, indicator, status, fileTypeLabel: text.fileTypeLabel },
    },
    title: { Component: FileItemTitleSlot, props: { disabled } },
    description: { Component: FileItemDescriptionSlot, props: { disabled, isLoading, loading: text.loading } },
    progress: { Component: FileItemProgressSlot, props: { value: progress, label: text.loading } },
    actions: { Component: FileItemActionsSlot },
  });

  // Show progress bar in place of description during upload (avoids layout shift)
  const showProgress = progress !== undefined && progress < 100;

  return (
    <div className={cn(fileItemVariants({ variant, disabled }), className)} data-spellbook-name="file-item" {...props}>
      {slots.fileType}
      <div className="flex flex-col min-w-0 flex-grow">
        {slots.title}
        {showProgress ? slots.progress : slots.description}
      </div>
      {slots.actions}
    </div>
  );
};

/**
 * Displays a file with its type, name, description, and actions in a compact card or row layout.
 *
 * ### When to use
 * - Displaying uploaded or attached files with metadata (name, type, size)
 * - Showing upload progress for individual files
 * - Providing file actions like download or remove
 * - Inside `Dropzone.FileList` for multi-file upload interfaces
 *
 * ### When NOT to use
 * - For file upload interaction: use `Dropzone` instead
 * - For media previews or image galleries: use a dedicated preview component
 *
 * ### API
 * - `variant`: `card` (default) for standalone use with border, `row` for use inside a parent container
 * - `disabled`: Visually disables the file item and makes actions non-interactive
 * - `fileType`: File extension (`pdf`, `docx`) or MIME type (`application/pdf`) — resolved internally
 * - `indicator`: `label` (default) shows uppercase text badge, `icon` shows file type icon
 * - `status`: `default` or `error`. Error state replaces file type with an error icon
 * - `isLoading`: Shows a loading spinner in place of the file type badge and disables actions
 * - `progress`: Upload progress value (0-100). When provided, renders a progress bar below the description
 * - `text`: Customizable text labels (e.g., `{ loading: 'Uploading...' }`)
 *
 * ### Composition (slots)
 * - `<FileItem.FileType>`: File type indicator (badge or icon). Rendered automatically from `fileType` prop — only include when customizing.
 * - `<FileItem.Title>`: Main file title/name. Rendered automatically — only include when customizing content.
 * - `<FileItem.Description>`: Additional file info (size, word count). Rendered automatically — only include when customizing.
 * - `<FileItem.Progress>`: Upload progress bar. Rendered automatically when `progress` prop is provided.
 * - `<FileItem.Actions>`: Container for action buttons. Only include when adding actions.
 * - `<FileItem.Action>`: Individual action button (IconButton with `size="sm"` and `variant="ghost"`).
 * - `<FileItem.DownloadAction>`: Preset download action with download icon.
 * - `<FileItem.RemoveAction>`: Preset remove action with trash icon and error color.
 *
 * ### Visual states
 * - **Default**: Standard card with border and background
 * - **Disabled**: Reduced opacity, muted border, `cursor-not-allowed`
 * - **Loading**: Spinner replaces file type indicator, description shows loading text
 * - **Error**: Error icon replaces file type indicator (when `indicator="icon"` and `status="error"`)
 * - **Progress**: Progress bar replaces description (when `progress` is 0-99)
 *
 * ### Accessibility
 * - Action buttons should have accessible labels via `aria-label` or wrapping `Tooltip`
 * - Pass `disabled` to action buttons inside `<FileItem.Actions>` when the FileItem is disabled
 */
export const FileItem = Object.assign(FileItemBase, {
  FileType: FileItemFileTypeSlot,
  Title: FileItemTitleSlot,
  Description: FileItemDescriptionSlot,
  Progress: FileItemProgressSlot,
  Actions: FileItemActionsSlot,
  Action: FileItemActionSlot,
  DownloadAction: FileItemDownloadActionSlot,
  RemoveAction: FileItemRemoveActionSlot,
});
